import React, { useState } from 'react';
import { ChevronDown, Play, Target, AlertTriangle, Dumbbell, Flame, Clock, Zap } from 'lucide-react';

const STATIONS = [
  {
    num: 1, name: 'SkiErg', distance: '1,000m', runKm: '1KM',
    color: '#e8ff00',
    tip: 'Keep a steady pace. Don\'t burn out in the first 200m.',
    videoId: '9RJiSvgaiJU',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/heww5odd_Daniel%20and%20Elli%20Individual%20Clips00415387.mov',
    form: [
      'Stand with feet hip-width apart, slight bend in knees',
      'Reach arms overhead and pull the handles down in a fluid motion',
      'Engage your core and hinge at the hips as arms pull past your thighs',
      'Let your arms float back up — don\'t muscle them back overhead',
    ],
    strategy: 'Start at 70-75% effort. The SkiErg is the first station and sets the tone for your entire race. Going too hard here will destroy your running splits for the next 7 laps.',
    mistakes: ['Starting too fast and blowing up by 500m', 'Pulling with arms only — the power should come from your lats and hips', 'Holding your breath instead of establishing a breathing rhythm'],
    drills: ['3x500m intervals at race pace with 90s rest', 'Tabata SkiErg: 20s on / 10s off x 8 rounds', '1,000m time trial once per week to track progress'],
    targetSplit: '4:00-5:30 depending on division',
  },
  {
    num: 2, name: 'Sled Push', distance: '50m', runKm: '2KM',
    color: '#E63946',
    tip: 'Drive from the hips. Short, powerful steps.',
    videoId: 'pk3Ha_pCArU',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/jn8rd8ib_Daniel%20and%20Elli%20Individual%20Clips00421357.mov',
    form: [
      'Grip the sled handles at chest height with arms locked out',
      'Keep your body at a 45-degree angle — lean into it',
      'Drive through your legs with short, choppy steps',
      'Keep your head down and eyes on the ground 2 feet ahead',
    ],
    strategy: 'The sled push is more technique than strength. Stay low, keep constant pressure, and don\'t stop. A steady push beats explosive bursts that stall you mid-lane.',
    mistakes: ['Standing too upright — you lose all leverage', 'Taking long strides instead of quick, short steps', 'Gripping too tight and wasting grip strength for later stations'],
    drills: ['Heavy sled pushes 4x50m with 2:00 rest', 'Prowler sprints: 10x25m at max effort', 'Wall sits 3x45s to build isometric leg endurance'],
    targetSplit: '1:30-3:00 depending on weight class',
  },
  {
    num: 3, name: 'Sled Pull', distance: '50m', runKm: '3KM',
    color: '#06B6D4',
    tip: 'Sit low, hand-over-hand. Use your legs.',
    videoId: 'ZBqWXVjdTls',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/jc2dfn6a_Daniel%20and%20Elli%20Individual%20Clips00424143.mov',
    form: [
      'Sit back into a low squat position — hips below knees if possible',
      'Grab the rope with alternating hands, pulling hand-over-hand',
      'Drive through your heels as you pull — legs do 70% of the work',
      'Keep constant tension on the rope — never let it go slack',
    ],
    strategy: 'This is a grip killer. Use legs as much as possible and develop a rhythm. When the sled gets close, stand up slightly and take 2-3 quick steps back to maintain momentum.',
    mistakes: ['Sitting too high — you need your legs in the game', 'Using only arms and frying your grip for farmers carry later', 'Letting the rope go slack between pulls'],
    drills: ['Rope climbs 3x3 for grip endurance', 'Seated cable rows 4x15 heavy', 'Hand-over-hand rope pulls with moderate weight 5x50m'],
    targetSplit: '1:30-3:30 depending on weight class',
  },
  {
    num: 4, name: 'Burpee Broad Jumps', distance: '80m', runKm: '4KM',
    color: '#F59E0B',
    tip: 'Find your rhythm. Don\'t rush the jumps.',
    videoId: 'W5gc1Inyha0',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/eoeovu73_Daniel%20and%20Elli%20Individual%20Clips00427672.mov',
    form: [
      'Drop to the ground with chest touching — full burpee required',
      'Push up explosively and jump forward in one fluid motion',
      'Swing arms forward to maximize distance on each jump',
      'Land with soft knees to absorb impact and save your joints',
    ],
    strategy: '80m of burpee broad jumps is a grind. Find a sustainable rhythm — think "down, up, jump" on a 3-second cycle. Longer jumps = fewer reps, but don\'t sacrifice form for distance.',
    mistakes: ['Going out too fast and hitting a wall at 40m', 'Short jumps that increase total reps needed', 'Not fully extending hips on the jump — lost distance every rep'],
    drills: ['5x16m burpee broad jumps with 60s rest', 'Burpee ladder: 1-2-3-4-5-4-3-2-1 reps', '80m time trial once per week at race pace'],
    targetSplit: '3:00-6:00 depending on fitness level',
  },
  {
    num: 5, name: 'Rowing', distance: '1,000m', runKm: '5KM',
    color: '#8B5CF6',
    tip: 'Drive with legs first. Aim for sub-2:00 splits.',
    videoId: 'ZX20U3Ix4jU',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/g910lzp9_Daniel%20and%20Elli%20Individual%20Clips00433345.mov',
    form: [
      'Strap feet in quickly — every second counts in transition',
      'Drive with legs first, then lean back, then pull arms to chest',
      'Recovery: arms away first, body follows, then bend knees',
      'Maintain a stroke rate of 26-30 SPM for optimal efficiency',
    ],
    strategy: 'Row at a pace you can sustain. For most athletes, this means 1:55-2:15 splits. The 1,000m row comes at the halfway point — save energy for the back half of the race.',
    mistakes: ['Pulling with arms first instead of driving legs', 'Stroke rate too high (35+) with no power — wasted energy', 'Not adjusting the damper setting (aim for 5-7)'],
    drills: ['4x500m at race pace with 90s rest', '10x100m sprints at max effort with 30s rest', '2,000m steady state rows at conversational pace 2x/week'],
    targetSplit: '3:30-5:00 depending on division',
  },
  {
    num: 6, name: 'Farmers Carry', distance: '200m', runKm: '6KM',
    color: '#10B981',
    tip: 'Grip hard, keep your core braced. Walk fast.',
    videoId: 'Rv8h4WoE1LA',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/d507xqjq_Daniel%20and%20Elli%20Individual%20Clips00435530.mov',
    form: [
      'Pick up kettlebells with a deadlift — protect your back',
      'Stand tall with shoulders packed down and back',
      'Brace your core like you\'re about to get punched',
      'Walk with quick, short steps — almost a shuffle',
    ],
    strategy: '200m is long. Your grip will fail if you go too fast or hold too tight. Find a fast walk pace you can maintain. If you must put them down, keep breaks under 5 seconds.',
    mistakes: ['Gripping too tight early and burning out by 100m', 'Leaning to one side — engages wrong muscles', 'Walking too slow — this should feel urgent, like a fast walk'],
    drills: ['Heavy farmers walks 4x100m with 90s rest', 'Dead hangs 3x max time to build grip endurance', 'Single-arm carries 3x50m each side for anti-rotation strength'],
    targetSplit: '1:30-3:00 depending on weight class',
  },
  {
    num: 7, name: 'Sandbag Lunges', distance: '100m', runKm: '7KM',
    color: '#EC4899',
    tip: 'Full range of motion. Knee to ground each rep.',
    videoId: '9CE0X-F0GKA',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/mwq1t9or_Daniel%20and%20Elli%20Individual%20Clips00438894.mov',
    form: [
      'Hug the sandbag high on your chest — don\'t let it drop to your waist',
      'Step forward and drop back knee to the ground (or just above)',
      'Drive through front heel to stand back up',
      'Alternate legs with each step — left, right, left, right',
    ],
    strategy: 'Sandbag lunges at station 7 will test your will. Your legs are destroyed. Focus on short, controlled steps. Every rep with a full range of motion counts — no shortcuts.',
    mistakes: ['Letting the sandbag slide down to your belly', 'Taking huge steps — shorter steps are more efficient', 'Not touching the knee down — judges will no-rep you'],
    drills: ['Walking lunges 3x50m with weight vest', 'Bulgarian split squats 3x12 each leg', 'Sandbag bear hugs + lunges 4x25m with 60s rest'],
    targetSplit: '2:00-4:30 depending on fitness level',
  },
  {
    num: 8, name: 'Wall Balls', distance: '75-100 reps', runKm: '8KM',
    color: '#e8ff00',
    tip: 'Use your legs to drive the ball. Catch and throw in one motion.',
    videoId: 'fpUD0MCFgps',
    stationVideo: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/y6y9g1i6_Daniel%20and%20Elli%20Individual%20Clips00441135.mov',
    form: [
      'Stand arm\'s length from the wall with feet shoulder-width apart',
      'Squat below parallel while holding the ball at chest height',
      'Drive up explosively and throw the ball to the target line',
      'Catch the ball on the way down and flow directly into the next squat',
    ],
    strategy: 'This is the last station. You\'re exhausted. Break it into sets: 25-25-25-25 or 20-20-20-20-20. Don\'t start with a set you can\'t finish. Consistent small sets beat one big set that gasses you.',
    mistakes: ['Not squatting deep enough — judges will no-rep you', 'Catching the ball too high and wasting energy lowering it', 'Stopping to rest with hands on knees — keep the ball moving'],
    drills: ['5x25 wall balls with 30s rest at race pace', 'Thrusters 4x15 with moderate weight', '100 wall balls for time once per week — track your PR'],
    targetSplit: '4:00-8:00 for 75-100 reps',
  },
];

export default function StationTrainingGuide() {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-3" data-testid="station-training-guide">
      {STATIONS.map((s) => (
        <div key={s.num} className="border border-zinc-800 bg-zinc-900/40 overflow-hidden transition-all duration-300 hover:border-zinc-700" data-testid={`station-card-${s.num}`}>
          {/* Collapsed Row */}
          <button
            onClick={() => setExpanded(expanded === s.num ? null : s.num)}
            className="w-full flex items-center gap-4 px-4 md:px-6 py-4 text-left group"
            data-testid={`station-toggle-${s.num}`}
          >
            {/* Run Km */}
            <div className="w-16 flex-shrink-0 text-center">
              <p className="text-zinc-600 text-[10px] uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>RUN</p>
              <p className="text-white text-xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.runKm}</p>
            </div>

            {/* Station Badge */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-[10px] font-bold uppercase tracking-wider"
                style={{ fontFamily: 'Bebas Neue, sans-serif', backgroundColor: `${s.color}20`, color: s.color, border: `1px solid ${s.color}40` }}
              >
                S{s.num}
              </span>
              <div className="min-w-0">
                <p className="text-white text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.name}</p>
              </div>
              <span className="text-zinc-500 text-xs ml-1 hidden sm:inline">{s.distance}</span>
            </div>

            {/* Tip */}
            <p className="text-zinc-500 text-xs hidden lg:block max-w-[300px] truncate">{s.tip}</p>

            {/* Expand Arrow */}
            <ChevronDown className={`w-4 h-4 text-zinc-600 flex-shrink-0 transition-transform duration-300 ${expanded === s.num ? 'rotate-180 text-[#e8ff00]' : 'group-hover:text-zinc-400'}`} />
          </button>

          {/* Expanded Content */}
          {expanded === s.num && (
            <div className="px-4 md:px-6 pb-6 space-y-6 border-t border-zinc-800/60 pt-4 animate-in slide-in-from-top-2" data-testid={`station-expanded-${s.num}`}>
              {/* Video + Strategy Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Station Video (cinematic) */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Play className="w-4 h-4" style={{ color: s.color }} />
                    <h4 className="text-white text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {s.stationVideo ? 'Station Film' : 'Form Guide'}
                    </h4>
                  </div>
                  {s.stationVideo ? (
                    <div className="relative w-full aspect-video bg-zinc-800 border border-zinc-700 overflow-hidden group" data-testid={`station-video-${s.num}`}>
                      <video
                        src={s.stationVideo}
                        controls
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="relative w-full aspect-video bg-zinc-800 border border-zinc-700 overflow-hidden">
                      <iframe
                        src={`https://www.youtube.com/embed/${s.videoId}?rel=0`}
                        title={`${s.name} Form Guide`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                    </div>
                  )}
                  <p className="text-zinc-600 text-[10px] mt-2 uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Target Split: {s.targetSplit}
                  </p>
                  {/* YouTube form guide link if station video is shown */}
                  {s.stationVideo && s.videoId && (
                    <a href={`https://www.youtube.com/watch?v=${s.videoId}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-zinc-500 text-[10px] uppercase tracking-wider hover:text-[#e8ff00] transition-colors"
                      style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      <Play className="w-3 h-3" /> Watch Form Guide on YouTube
                    </a>
                  )}
                </div>

                {/* Form Checklist */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4" style={{ color: s.color }} />
                    <h4 className="text-white text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Proper Form</h4>
                  </div>
                  <div className="space-y-2.5">
                    {s.form.map((f, i) => (
                      <div key={f} className="flex gap-3 items-start">
                        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[9px] font-bold mt-0.5" style={{ backgroundColor: `${s.color}15`, color: s.color, border: `1px solid ${s.color}30` }}>{i + 1}</span>
                        <p className="text-zinc-300 text-sm leading-relaxed">{f}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Strategy */}
              <div className="bg-zinc-800/40 border border-zinc-700/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" style={{ color: s.color }} />
                  <h4 className="text-white text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Race Strategy</h4>
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">{s.strategy}</p>
              </div>

              {/* Mistakes + Drills Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Common Mistakes */}
                <div className="bg-red-500/5 border border-red-500/15 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <h4 className="text-red-400 text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Common Mistakes</h4>
                  </div>
                  <ul className="space-y-2">
                    {s.mistakes.map((m, i) => (
                      <li key={m} className="flex gap-2 items-start text-sm text-zinc-400">
                        <span className="text-red-500 mt-1 flex-shrink-0">&times;</span>
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Training Drills */}
                <div className="bg-green-500/5 border border-green-500/15 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Dumbbell className="w-4 h-4 text-green-400" />
                    <h4 className="text-green-400 text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Training Drills</h4>
                  </div>
                  <ul className="space-y-2">
                    {s.drills.map((d, i) => (
                      <li key={d} className="flex gap-2 items-start text-sm text-zinc-400">
                        <span className="text-green-500 mt-1 flex-shrink-0">&#8594;</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
